import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, Wifi, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const ModelosAPPage = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('config', 'modelos_ap');
  const [modelos, setModelos] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', marca_id: '' });

  // Modals
  const [editModal, setEditModal] = useState({ open: false, modelo: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, modelo: null });
  const [formData, setFormData] = useState({
    modelo: '', description: '', marca_id: '', last_firmware: ''
  });
  const [saving, setSaving] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState('modelo');
  const [sortOrder, setSortOrder] = useState('asc');
  const sortedModelos = [...modelos].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchModelos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.marca_id) params.append('marca_id', filters.marca_id);

      const response = await api.get(`/config/modelos-ap?${params}`);
      setModelos(response.data.items);
      setMarcas(response.data.marcas || []);
    } catch (error) {
      toast.error('Error al cargar modelos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchModelos();
  }, [filters.search, filters.marca_id]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Marca badge
  const getMarcaBadge = (marcaNombre) => {
    if (!marcaNombre) return <span className="badge badge-outline">Sin marca</span>;
    const m = marcaNombre.toLowerCase();
    if (m.includes('ubiquiti')) return <span className="badge badge-info">{marcaNombre}</span>;
    if (m.includes('cisco') || m.includes('meraki')) return <span className="badge badge-success">{marcaNombre}</span>;
    if (m.includes('aruba')) return <span className="badge badge-warning">{marcaNombre}</span>;
    if (m.includes('tp-link')) return <span className="badge badge-secondary">{marcaNombre}</span>;
    if (m.includes('ruckus')) return <span className="badge badge-info">{marcaNombre}</span>;
    if (m.includes('fortinet')) return <span className="badge badge-error">{marcaNombre}</span>;
    return <span className="badge badge-outline">{marcaNombre}</span>;
  };

  // Edit modal
  const handleEdit = (modelo) => {
    setFormData({
      modelo: modelo.modelo || '',
      description: modelo.description || '',
      marca_id: modelo.marca_id?.toString() || '',
      last_firmware: modelo.last_firmware || ''
    });
    setEditModal({ open: true, modelo });
  };

  // Create new
  const handleCreate = () => {
    setFormData({ modelo: '', description: '', marca_id: '', last_firmware: '' });
    setEditModal({ open: true, modelo: null });
  };

  // Save
  const handleSave = async () => {
    if (!formData.modelo.trim()) {
      toast.error('El nombre del modelo es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        marca_id: formData.marca_id ? parseInt(formData.marca_id) : null
      };

      if (editModal.modelo) {
        await api.put(`/config/modelos-ap/${editModal.modelo.id}`, payload);
        toast.success('Modelo actualizado exitosamente');
      } else {
        await api.post('/config/modelos-ap', payload);
        toast.success('Modelo creado exitosamente');
      }
      setEditModal({ open: false, modelo: null });
      fetchModelos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/config/modelos-ap/${deleteModal.modelo.id}`);
      toast.success('Modelo eliminado exitosamente');
      setDeleteModal({ open: false, modelo: null });
      fetchModelos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Modelos de AP</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Modelos Access Points</h1>
            <p className="page-subtitle">Catalogo de modelos de puntos de acceso</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-modelo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Modelo
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar modelo..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-9"
              data-testid="filter-search"
            />
          </div>
          <Select
            value={filters.marca_id || "todas"}
            onValueChange={(v) => handleFilterChange('marca_id', v === "todas" ? "" : v)}
          >
            <SelectTrigger className="w-[180px]" data-testid="filter-marca">
              <SelectValue placeholder="Todas las marcas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las marcas</SelectItem>
              {marcas.map(marca => (
                <SelectItem key={marca.id} value={marca.id.toString()}>{marca.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('modelo')}><div className="flex items-center gap-1.5">MODELO <SortIcon field="modelo" /></div></th>
                      <th className="text-center cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('marca_nombre')}><div className="flex items-center justify-center gap-1.5">MARCA <SortIcon field="marca_nombre" /></div></th>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('description')}><div className="flex items-center gap-1.5">DESCRIPCION <SortIcon field="description" /></div></th>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('last_firmware')}><div className="flex items-center gap-1.5">ULTIMO FIRMWARE <SortIcon field="last_firmware" /></div></th>
                      <th className="w-28 text-center">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedModelos.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state">
                            <Wifi className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                            <p className="empty-state-text">No se encontraron modelos</p>
                          </div>
                        </td>
                      </tr>
                    ) : sortedModelos.map((modelo) => (
                      <tr key={modelo.id} data-testid={`modelo-row-${modelo.id}`}>
                        <td className="font-medium text-primary">{modelo.modelo}</td>
                        <td className="text-center">{getMarcaBadge(modelo.marca_nombre)}</td>
                        <td className="text-muted-foreground">{modelo.description || '-'}</td>
                        <td className="font-mono text-sm text-muted-foreground">{modelo.last_firmware || '-'}</td>
                        <td className="w-28">
                          <div className="flex justify-center gap-0.5">
                            <button
                              onClick={() => canUpdate && handleEdit(modelo)}
                              className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                              disabled={!canUpdate}
                              title="Editar"
                              data-testid={`edit-modelo-${modelo.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => canDelete && setDeleteModal({ open: true, modelo })}
                              className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                              disabled={!canDelete}
                              title="Eliminar"
                              data-testid={`delete-modelo-${modelo.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {modelos.length} modelo{modelos.length !== 1 ? 's' : ''}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit/Create Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, modelo: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-primary" />
                {editModal.modelo ? 'Editar Modelo' : 'Nuevo Modelo'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Modelo *</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Ej: UAP-AC-PRO"
                  data-testid="input-modelo"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select
                  value={formData.marca_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, marca_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-marca">
                    <SelectValue placeholder="Seleccionar marca" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin marca</SelectItem>
                    {marcas.map(marca => (
                      <SelectItem key={marca.id} value={marca.id.toString()}>{marca.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Descripcion</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripcion del modelo"
                  data-testid="input-description"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ultimo Firmware</Label>
                <Input
                  value={formData.last_firmware}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_firmware: e.target.value }))}
                  placeholder="Ej: 6.5.28"
                  data-testid="input-firmware"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, modelo: null })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, modelo: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Modelo
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estas seguro que deseas eliminar el modelo <strong>{deleteModal.modelo?.modelo}</strong>?
            </p>
            <p className="text-sm text-yellow-600">
              No se podra eliminar si hay Access Points usando este modelo.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, modelo: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ModelosAPPage;
